import {memo, useCallback, useEffect, useMemo, useState} from 'react';
import PageBlock from "../../../components/PageBlock";
import {Button, CardBody, Menu, MenuHandler, MenuItem, MenuList} from "@material-tailwind/react";
import Card from "../../../components/Card";
import {useToastContext} from "../../../types/contexts/ToastContext";
import {useErrorHandler} from "react-error-boundary";
import {UsersResponse} from "../../../types/Responses/UsersResponse";
import {RolesShortResponse} from "../../../types/Responses/RolesAndPermissions";
import {DataTableRequest} from "../../../types/Requests/API/DataTableRequest";
import {API} from "../../../types/API";
import {AxiosResponse} from "axios";
import {ErrorHandler} from "../../../hooks/errorHandler";
import TableSkeleton from "../../../components/Skeletons/TableSkeleton";
import DataTable from "../../../components/DataTable/DataTable";
import TD from "../../../components/DataTable/TD";
import BrokerAvatar from "../../../components/BrokerAvatar";
import {useUserContext} from "../../../types/contexts/UserContext";
import {useNavigate} from "react-router-dom";
import StatusLabel from "../../../components/StatusLabel";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {User} from "../../../types/User";
import useComponentVisible from "../../../hooks/useComponentVisible";
import Modal from "../../../components/Modal/Modal";
import {useTranslation} from "react-i18next";
import {setTitle} from "../../../hooks/setTitle";
import {APILink} from "../../../types/APILink";
import {checkEnabled2FA} from "../../../hooks/checkEnabled2FA";
import {useSystemContext} from "../../../types/contexts/SystemContext";
import usePersistedDataTableRequest from "../../../hooks/usePersistedDataTableRequest";
import {useDispatch} from "react-redux";
import {setSidebarTitle} from "../../../reducers/sidebar";
import DropdownInput from "../../../components/DropdownInput";
import FilterChips, {FilterChipDefinition} from "../../../components/FilterChips";

type UsersDataTableRequest = DataTableRequest & { role?: number, status?: number };

const Users = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const system = useSystemContext();
    const user = useUserContext();
    const navigate = useNavigate();
    const toast = useToastContext();
    const handleError = useErrorHandler();
    const [users, setUsers] = useState<UsersResponse>();
    const [roles, setRoles] = useState<RolesShortResponse>();
    const [modalConfirmUser, setModalConfirmUser] = useState<User>();
    const [isDataTableLoading, setIsDataTableLoading] = useState<boolean>(false);
    const {ref, isComponentVisible, setIsComponentVisible} = useComponentVisible(false);
    const defaultRequest = useMemo<UsersDataTableRequest>(() => ({
        length: 10,
        page: 1,
        sort_by: 'id',
        sort_direction: 1
    }), []);
    const [dataTableRequest, persistDataTableRequest] = usePersistedDataTableRequest<UsersDataTableRequest>(defaultRequest);
    const filterRole = dataTableRequest.role ?? 0;
    const filterStatus = dataTableRequest.status ?? 0;

    const clearRequest = (request: DataTableRequest | object) => {
        const keys = Object.keys(request);
        if (keys.includes('role') && typeof request['role'] !== "undefined" && parseInt(request['role']) === 0)
            delete request['role'];
        if (keys.includes('status') && typeof request['status'] !== "undefined" && parseInt(request['status']) === 0)
            delete request['status'];
        return request;
    }

    const sendRequest = useCallback((request: object) => {
        setIsDataTableLoading(true);
        API.getUsers(clearRequest(request)).then((response: AxiosResponse) => {
            setUsers(response.data as UsersResponse);
            setIsDataTableLoading(false);
        }).catch(e => ErrorHandler(e, `get${APILink.GET_USERS}`, toast, handleError));
    }, [toast, handleError]);

    const sendRolesRequest = useCallback(() => {
        API.getShortRoles().then((response: AxiosResponse) => {
            setRoles(response.data as RolesShortResponse);
        }).catch(e => ErrorHandler(e, `get${APILink.GET_SHORT_ROLES}`, toast, handleError));
    }, [toast, handleError]);

    const onDataTableChanged = useCallback((request: UsersDataTableRequest) => {
        const updatedRequest = {
            ...request,
            role: filterRole,
            status: filterStatus,
        };
        persistDataTableRequest(updatedRequest);
        if (!request.search || request.search.length > 3) {
            const normalized = !request.search ? {...updatedRequest, search: ''} : updatedRequest;
            sendRequest(normalized);
        }
    }, [persistDataTableRequest, filterRole, filterStatus, sendRequest]);

    const onRoleChanged = useCallback((u: string | number) => {
        const parsedRole = typeof u === "string" ? parseInt(u) : u;
        const request = {
            ...dataTableRequest,
            page: 1,
            role: parsedRole,
            status: filterStatus,
        };
        persistDataTableRequest(request);
        sendRequest(request);
    }, [filterStatus, dataTableRequest, sendRequest, persistDataTableRequest]);

    const onSelectAllRoles = useCallback(() => {
        const request = {
            ...dataTableRequest,
            page: 1,
            role: undefined,
            status: filterStatus,
        };
        persistDataTableRequest(request);
        sendRequest(request);
    }, [filterStatus, dataTableRequest, sendRequest, persistDataTableRequest]);

    const onStatusChanged = useCallback((s: string | number) => {
        console.log(s)
        const st = typeof s === 'string' ? parseInt(s) : s;
        const request = {
            ...dataTableRequest,
            page: 1,
            status: st,
            role: filterRole,
        };
        persistDataTableRequest(request);
        sendRequest(request);
    }, [filterRole, dataTableRequest, sendRequest, persistDataTableRequest]);

    const onSelectAllStatuses = useCallback(() => {
        const request = {
            ...dataTableRequest,
            page: 1,
            status: undefined,
            role: filterRole,
        };
        persistDataTableRequest(request);
        sendRequest(request);
    }, [filterRole, dataTableRequest, sendRequest, persistDataTableRequest])

    const sendDeleteRequest = useCallback((u: User) => {
        if (users) {
            setIsDataTableLoading(true);
            API.deleteUser(u.id).then(() => {
                const filteredArray = {
                    ...users,
                    data: users.data.map((item) => {
                        if (item.id === u.id)
                            item.status = 'Inactive';
                        return item;
                    }),
                };
                setUsers(filteredArray);
                setIsDataTableLoading(false);
                setIsComponentVisible(false);
            }).catch(e => ErrorHandler(e, `delete${APILink.GET_USER.replace('$user', u.id.toString())}`, toast, handleError));
        }
    }, [users, toast, handleError, setUsers, setIsDataTableLoading]);

    const deleteUser = useCallback((u: User) => {
        setModalConfirmUser(u);
        setIsComponentVisible(true);
    }, [setModalConfirmUser, setIsComponentVisible]);

    const closeModal = useCallback(() => {
        setIsComponentVisible(false);
    }, [setIsComponentVisible]);

    const reset2FA = useCallback((u: User) => {
        API.reset2FA(u.id).then(() => {
            toast?.show(<><FontAwesomeIcon icon={solid("circle-check")}
                                           className="mr-4"/>{t('admin.users.reset.title', {name: u.first_name + ' ' + u.last_name})}</>, "bg-teal-500");
            sendRequest({
                ...dataTableRequest,
                role: filterRole,
                status: filterStatus
            });
        }).catch(e => ErrorHandler(e, `post${APILink.RESET_2FA.replace('$user', u.id.toString())}`, toast, handleError));
    }, [toast, handleError, filterRole, filterStatus, dataTableRequest, sendRequest]);

    const resetBrokerToken = useCallback((u: User) => {
        const el = document.createElement("textarea");
        if (el) {
            API.resetBrokerToken(u.id).then((response: AxiosResponse) => {
                if (response.data.data.token) {
                    el.value = response.data.data.token;
                    document.body.appendChild(el);
                    el.select();
                    document.execCommand("copy");
                    document.body.removeChild(el);
                    toast?.show(<><FontAwesomeIcon icon={solid("circle-check")}
                                                   className="mr-4"/>{t('admin.users.regenerate_message')}</>, "bg-teal-500");
                }
            }).catch(e => ErrorHandler(e, `post${APILink.RESET_BROKER_TOKEN.replace('$user', u.id.toString())}`, toast, handleError));
        }

    }, [toast, handleError, filterRole, filterStatus, dataTableRequest, sendRequest]);

    const refresh = useCallback(() => {
        sendRequest({...dataTableRequest});
    }, [dataTableRequest]);

    useEffect(() => {
        setTitle(t('admin.users.title'));
        dispatch(setSidebarTitle(t('admin.users.title')));
        if (user && user.user) {
            checkEnabled2FA(user.user).then(() => {
                sendRolesRequest();
                const locationSearch = window.location.search.substr(1).split('&');
                if (locationSearch.length > 0) {
                    const newID = locationSearch[0]?.split('=');
                    if (newID?.length) {
                        const request = {...dataTableRequest, search: newID[1]};
                        persistDataTableRequest(request);
                        sendRequest(request);
                        return;
                    }
                }
                sendRequest(dataTableRequest);
            }).catch()
        }
    }, [user, dispatch, t, setSidebarTitle]);

    const filterChipsItems = useMemo<FilterChipDefinition[]>(() => {
        const chips: FilterChipDefinition[] = [];
        if (roles?.data && filterRole > 0) {
            const selectedRole = roles.data.find((role) => role.id === filterRole);
            if (selectedRole) {
                chips.push({
                    key: `role-${selectedRole.id}`,
                    label: `${t('admin.users.role')}: ${selectedRole.name}`,
                    onRemove: onSelectAllRoles
                });
            }
        }
        if (system?.system?.user_statuses && filterStatus > 0) {
            const status = system.system.user_statuses.find((s) => s.id === filterStatus);
            if (status) {
                chips.push({
                    key: `status-${status.id}`,
                    label: `${t('admin.languages.table.status')}: ${status.name}`,
                    onRemove: onSelectAllStatuses
                });
            }
        }
        return chips;
    }, [roles, filterRole, t, onSelectAllRoles, system, filterStatus, onSelectAllStatuses]);

    const filters = useMemo(() => {
        if (!users || !roles || !system || !system.system)
            return <></>;

        return <>
            <DropdownInput
                withAllButton={true}
                name={t('admin.users.role')}
                selected={filterRole}
                onChange={onRoleChanged}
                onSelectAll={onSelectAllRoles}
                list={roles.data}
                menuClassName="w-full -mt-5"
            />

            <DropdownInput
                withAllButton={true}
                name={t('admin.languages.table.status')}
                selected={filterStatus}
                onChange={onStatusChanged}
                onSelectAll={onSelectAllStatuses}
                list={system.system.user_statuses}
                menuClassName="w-full -mt-5"
            />
        </>
    }, [users, roles, system, t, filterRole, onRoleChanged, onSelectAllRoles, onSelectAllStatuses, filterStatus, onStatusChanged]);

    const actionsPanel = useMemo(() => {
        return (<div className="flex flex-row gap-2 lg:justify-end">
                <Button className="bg-transparent text-black dark:text-white shadow-none px-0.5"
                        onClick={() => refresh()}>
                    <FontAwesomeIcon
                        icon={solid('refresh')}
                        className="w-12 h-3"/>
                </Button>
                {user?.user.permissions.includes('create user') ?
                    <Button onClick={() => navigate('/app/admin/users/create')}
                            className="bg-black text-white">{t('admin.users.new.button')}</Button> : ''
                }
            </div>
        )
    }, [user, refresh]);

    const dataTable = useMemo(() => {
        if (!users || typeof users.data === 'undefined')
            return <TableSkeleton/>;

        return <DataTable {...dataTableRequest}
                          total_pages={users.pages.total}
                          count={users.count}
                          nodata={users.data.length === 0}
                          filters={filters}
                          fullWidthFilters={<FilterChips className="px-4 -mt-6 mb-6" items={filterChipsItems}/>}
                          inProgress={isDataTableLoading}
                          onChange={onDataTableChanged}
                          rightPanel={actionsPanel}
                          headers={[
                              {name: '#', sortBy: 'id'},
                              {name: t('admin.users.table.name'), sortBy: 'id'},
                              {name: t('admin.users.table.email'), sortBy: 'email'},
                              {name: t('admin.users.table.roles')},
                              {name: t('admin.users.table.status'), sortBy: 'status_id'},
                              {name: t('admin.users.table.last_login'), sortBy: 'last_activity_at'},
                              {name: t('admin.users.table.two_fa')},
                              {name: '', className: 'text-right'}
                          ]}>
            <tbody>
            {users.data.map((tableUser, index) => {
                    return <tr key={index}>
                        <TD>{((dataTableRequest.page - 1) * dataTableRequest.length) + index + 1}</TD>
                        <TD className="flex items-center justify-center">
                            <>
                                <BrokerAvatar image={tableUser.image}/>
                                <a href={`${window.location.origin}/app/admin/users/${tableUser.id}/profile`}
                                   className="hover:text-black dark:hover:text-white">{tableUser.first_name} {tableUser.last_name}</a>
                            </>
                        </TD>
                        <TD>{tableUser.email}</TD>
                        <TD>{tableUser.roles?.join(',') ?? ''}</TD>
                        <TD><StatusLabel status={tableUser.status}/></TD>
                        <TD>{tableUser.last_activity_at}</TD>
                        <TD>{tableUser.two_fa ? t('Enabled') : t('Disabled')}</TD>
                        <TD className="text-right">
                            <>
                                {tableUser.roles.includes('Broker') && user?.user.permissions.includes('view broker payment methods') &&
                                    <Button
                                        className="bg-[#f6f7f8!important] dark:bg-[#455b64!important] text-black dark:text-white mr-2 px-5 py-2 shadow-none"
                                        onClick={() => navigate('/app/admin/users/' + tableUser.id + '/payment_methods')}>
                                        <FontAwesomeIcon icon={solid('money-bill')} className="w-5"/>
                                    </Button>}
                                <Menu>
                                    <MenuHandler>
                                        <Button className="text-white bg-black mr-2 px-5 py-2"><FontAwesomeIcon
                                            icon={solid('gear')}/></Button>
                                    </MenuHandler>
                                    <MenuList className="dark:bg-blue-gray-900 dark:text-white dark:border-blue-gray-500">
                                        {!tableUser.roles.includes('Admin') && tableUser.broker_id && user?.user.permissions.includes('view deposit transactions list') &&
                                            <MenuItem
                                                onClick={() => navigate('/app/admin/users/' + tableUser.broker_id + '/deposits')}>
                                                <FontAwesomeIcon icon={solid('file-invoice')}
                                                                 className="mr-2 text-teal-500 w-5"/>
                                                <span>{t('admin.users.deposit_transactions')}</span>
                                            </MenuItem>}
                                        {!tableUser.roles.includes('Admin') && tableUser.broker_id && user?.user.permissions.includes('view withdraw transactions list') &&
                                            <MenuItem
                                                onClick={() => navigate('/app/admin/users/' + tableUser.broker_id + '/withdrawals')}>
                                                <FontAwesomeIcon icon={solid('file-invoice')}
                                                                 className="mr-2 text-red-300 w-5"/>
                                                <span>{t('admin.users.withdraw_transactions')}</span>
                                            </MenuItem>}

                                        {(user?.user.permissions.includes('update user') || user?.user.permissions.includes('delete user')) && !tableUser.roles.includes('Admin') &&
                                            <hr className="my-3 dark:border-blue-gray-500 outline-none"/>}

                                        {user?.user.permissions.includes('regenerate broker token') && tableUser.roles.includes('Broker') &&
                                            <MenuItem
                                                onClick={() => resetBrokerToken(tableUser)}>
                                                <FontAwesomeIcon icon={solid('handshake')} className="mr-2 w-5"/>
                                                <span>{t('admin.users.reset_token')}</span>
                                            </MenuItem>}

                                        {user?.user.roles.includes('Admin') && tableUser.two_fa && <MenuItem
                                            onClick={() => reset2FA(tableUser)}>
                                            <FontAwesomeIcon icon={solid('fingerprint')} className="mr-2 w-5"/>
                                            <span>{t('admin.users.reset')}</span>
                                        </MenuItem>}

                                        {user?.user.permissions.includes('update user') &&
                                            <MenuItem onClick={() => navigate('/app/admin/users/' + tableUser.id)}>
                                                <FontAwesomeIcon icon={solid("pen-to-square")} className="w-5 mr-2"/>
                                                <span>{t('admin.users.edit')}</span>
                                            </MenuItem>
                                        }
                                        {user?.user.permissions.includes('delete user') && tableUser.status === 'Active' &&
                                            <MenuItem onClick={() => deleteUser(tableUser)}>
                                                <FontAwesomeIcon icon={solid("trash")} className="w-5 mr-2 text-red-300"/>
                                                <span>{t('admin.users.delete.btn')}</span>
                                            </MenuItem>
                                        }
                                    </MenuList>
                                </Menu>
                            </>
                        </TD>
                    </tr>
                }
            )}
            </tbody>
        </DataTable>
    }, [users, dataTableRequest, filters, filterChipsItems, isDataTableLoading, onDataTableChanged, t, user?.user.permissions, user?.user.roles, navigate, resetBrokerToken, reset2FA, deleteUser]);

    const modalUI = useMemo(() => {
        if (!modalConfirmUser || !users || !isComponentVisible)
            return <></>;
        return <Modal refer={ref}
                      showModal={isComponentVisible}
                      title={t('admin.users.delete.title', {name: modalConfirmUser.first_name + ' ' + modalConfirmUser.last_name})}>
            <>
                <p>{t('admin.users.delete.message', {name: modalConfirmUser.first_name + ' ' + modalConfirmUser.last_name})}</p>
                <Button className="mt-8" color="teal"
                        onClick={() => sendDeleteRequest(modalConfirmUser)}>{t('admin.users.confirm')}</Button>
                <span className="mt-1 cursor-pointer text-gray-400 dark:text-gray-700"
                      onClick={() => closeModal()}>{t('close')}</span>
            </>
        </Modal>
    }, [modalConfirmUser, users, isComponentVisible, closeModal]);

    return (
        <PageBlock>
            <Card className="page-card-block mt-6 bg-white dark:bg-blue-gray-900 dark:text-gray-500">
                <CardBody>
                    {modalUI}
                    {dataTable}
                </CardBody>
            </Card>
        </PageBlock>
    );
}
export default memo(Users);
